<script>
	import { useForm } from 'svelte-use-form';
	import { validator, email, emailCheckbox } from '$lib/utils/form-validations';
	import { writable } from 'svelte/store';
	import { createEventDispatcher } from 'svelte';
	import { closeModal } from 'svelte-modals';

	export let content = {
		title: '',
		subtitle: '',
		disclaimer: '',
		placeholder: '',
		errorMessage: '',
		agreeText: '',
		agreeError: '',
		privacyLink: {
			text: '',
			url: '',
			alt: ''
		},
		buttonText: ''
	};
	export let isProspectRegisterModal = false;

	let disableForm = false;
	let isFormSubmitted = false;
	let credentialsForm = writable({ email: '' });

	const dispatchEvent = createEventDispatcher();

	const form = useForm({
		email: {
			initial: $credentialsForm.email,
			validators: [validator(email)]
		},
		termAgreement: {
			initial: 'checked',
			validators: [validator(emailCheckbox)]
		}
	});

	/**
	 *
	 */
	const validateForm = () => {
		isFormSubmitted = true;
		$form.email.validate();
		$form.termAgreement.validate();

		return $form.valid;
	};

	/**
	 * @param {{ currentTarget: HTMLFormElement | undefined; }} e
	 */
	function submitForm(e) {
		disableForm = true;
		if (!validateForm()) {
			return (disableForm = false);
		}

		credentialsForm.set({
			email: $form.email.value
		});
		dispatchEvent('submit', { email: $form.email.value });
		disableForm = false;
	}
</script>

<section class="flex w-full flex-col items-center justify-items-center">
	<div
		class="relative w-[30rem] bg-[url('/assets-mkt/routes/home/mini_reg_bg-v2.png')] bg-cover bg-no-repeat sm:w-[60rem] md:w-[72rem]"
	>
		{#if isProspectRegisterModal}
			<button
				type="button"
				class="absolute left-[1.5rem] top-[6rem] z-[20] h-[1.6rem] w-[1.6rem] sm:left-[3rem] sm:top-[11rem] sm:h-[2.6rem] sm:w-[2.6rem] md:left-[3rem] md:top-[13rem]"
				on:click|preventDefault={() => closeModal()}
				><img src="/assets-mkt/routes/subscription/x_icon_v1.png" alt="Clear" />
			</button>
		{/if}
		<form
			class="flex flex-col items-center sm:w-[45rem]"
			use:form
			on:submit|preventDefault={submitForm}
			disabled={disableForm}
			novalidate
		>
			<div
				class="ml-[-5rem] mt-[5.5rem] w-[18.5rem] sm:ml-[4rem] sm:mt-[11rem] sm:w-[36.2rem] md:mt-[13rem] md:w-[51rem]"
			>
				<h2
					class="bg-gradient-to-r from-[#ff8e44] to-[#f91362] bg-clip-text text-right text-[3.3rem] font-extrabold leading-[unset] tracking-normal text-transparent sm:text-[6.7rem] md:text-[8rem]"
				>
					{content.title}
				</h2>
				<h3
					class="mt-[-1.6rem] bg-gradient-to-r from-[#ff8e44] to-[#f91362] bg-clip-text text-right text-[1.7rem] font-semibold leading-[unset] text-transparent sm:mt-[-2.5rem] sm:text-[3.3rem] md:text-[4rem]"
				>
					{content.subtitle}
				</h3>
				<p
					class="mr-[-1rem] text-right font-poppins text-[0.95rem] text-[#474141] sm:mb-[1.2rem] sm:text-[1.8rem] md:text-[2.3rem]"
				>
					{content.disclaimer}
				</p>
			</div>
			<div class="my-2 flex flex-col items-center">
				<div
					class="flex w-[22rem] flex-col items-center sm:ml-[7rem] sm:w-[38rem] md:ml-[16rem] md:w-[45rem]"
				>
					<div class="w-full flex-1">
						<input
							data-track-id="email"
							class:invalid={!$form.email.valid && ($form.email.touched || isFormSubmitted)}
							class="input-primary h-[2.5rem] w-full rounded-[.6rem] border border-[#2e7cf6] pl-[1rem] pr-[3rem] font-poppins leading-[3.5rem] text-[#767676] sm:h-[4.5rem] sm:pr-[3.5rem] md:h-[5.3rem]"
							type="email"
							id="email"
							name="email"
							placeholder={content.placeholder}
							on:blur={() => $form.email.validate()}
							autocomplete="email"
						/>
						<div
							class="h-[1.2rem] cursor-auto font-poppins text-[.7rem] text-[#fa3260] sm:text-[1.2rem]"
						>
							{#if !$form.email?.valid && ($form.email.touched || isFormSubmitted)}
								<p class="text-right">
									{$form.email?.errors?.message}
								</p>
							{/if}
						</div>
					</div>

					<div class="flex items-start gap-2 sm:ml-[8rem] sm:mt-[1rem] sm:w-[39rem] md:w-[47rem]">
						<input
							name="termAgreement"
							type="checkbox"
							id={isProspectRegisterModal ? 'checkbox-agreement-modal' : 'checkbox-agreement'}
							value="checked"
							checked={$form.termAgreement.value === 'checked'}
							formnovalidate
							class="checkbox-primary-red h-[1.5rem] w-[1.7rem] border-[.1rem] border-[#767676] bg-white sm:h-[2.4rem] sm:w-[2.8rem] md:h-[3rem] md:w-[3.3rem]"
						/>
						<label
							for={isProspectRegisterModal ? 'checkbox-agreement-modal' : 'checkbox-agreement'}
							class="flex w-full cursor-pointer items-center"
						>
							<span
								class="font-poppins text-[.8rem] font-medium text-[#515151] sm:text-[1.7rem] sm:font-regular md:text-[2rem]"
							>
								{content.agreeText}
							</span>
						</label>
					</div>

					<div
						class="h-[1.2rem] w-full text-center font-poppins text-[.7rem] font-regular text-[#fa3260] sm:ml-[8rem] sm:h-[2.5rem] sm:text-[1.2rem]"
					>
						{#if !$form.termAgreement?.valid && ($form.termAgreement.touched || isFormSubmitted)}
							{$form.termAgreement?.errors?.message}
						{/if}
					</div>

					<button
						id="nmhp-register-submit"
						type="submit"
						class="btn-primary-red h-[3.1rem] w-[13.3rem] cursor-pointer font-poppins text-[1rem] font-semibold sm:ml-[8rem] sm:h-[4.5rem] sm:w-[26.5rem] sm:text-[2rem] md:h-[5.3rem] md:w-[31.5rem] md:text-[2.3rem]"
						disabled={disableForm}>{content.buttonText}</button
					>
				</div>
			</div>
		</form>

		<div class="mb-[2rem] flex w-full justify-center sm:mb-[6rem]">
			<a
				id="nmhp-pp-link"
				class="text-center text-[.8rem] text-[#fa3260] underline sm:mt-[1.1rem] sm:text-[1.3rem] md:mt-[3.1rem] md:text-[1.5rem]"
				href={content.privacyLink.url}
				aria-label={content.privacyLink.alt}>{content.privacyLink.text}</a
			>
		</div>
	</div>
</section>
